import { createSlice } from '@reduxjs/toolkit';

import {
  getManagementDataset,
  collectData,
  deleteFromDataset,
  getSonarProgress,
  importDataset,
  getDatasetItem,
  editDataset,
} from './sonarManagementActions';
import { SONAR_PROGRESS_TYPE } from '../../constants';
import mergeArraysToOptions from '../../utils/mergeArraysToOptions';

const initialState = {
  isCollectingData: false,
  isDatasetLoading: false,
  dataset: [],
  isAllSelected: false,

  editableDataset: {},
  isEditableDatasetLoading: false,

  datasetErrors: {},

  sonarProgress: {
    [SONAR_PROGRESS_TYPE.CRAWL]: {
      id: null,
      status: null,
      handled: 0,
      total: 0,
    },
    [SONAR_PROGRESS_TYPE.COLLECT_SONAR]: {
      id: null,
      status: null,
      currentProgress: {},
      total: 0,
    },
  },

  groupsOptions: [],
  categoriesOptions: [],
  productsOptions: [],
  servicesOptions: [],
};

const sonarManagementSlice = createSlice({
  name: 'sonarManagement',
  initialState,
  reducers: {
    toggleAllDomains: (state) => {
      state.dataset = state.dataset.map((el) => ({ ...el, isSelected: !state.isAllSelected }));
      state.isAllSelected = !state.isAllSelected;
    },
    selectSingleDomain: (state, action) => {
      state.dataset = state.dataset.map((el) =>
        el.id === action.payload ? { ...el, isSelected: !el.isSelected } : { ...el },
      );
    },

    addNewCategoryOption: (state, action) => {
      state.categoriesOptions = [...state.categoriesOptions, action.payload];
    },
    addNewGroupOption: (state, action) => {
      state.groupsOptions = [...state.groupsOptions, action.payload];
    },
    addNewServiceOption: (state, action) => {
      state.servicesOptions = [...state.servicesOptions, action.payload];
    },
    addNewProductOption: (state, action) => {
      state.productsOptions = [...state.productsOptions, action.payload];
    },
    updateDatasetOptions: (state, action) => {
      const datasetItems = action.payload;

      state.groupsOptions = mergeArraysToOptions(
        state.groupsOptions,
        datasetItems.map((item) => item.groups),
      );
      state.categoriesOptions = mergeArraysToOptions(
        state.categoriesOptions,
        datasetItems.map((item) => item.categories),
      );
      state.productsOptions = mergeArraysToOptions(
        state.productsOptions,
        datasetItems.map((item) => item.products),
      );
      state.servicesOptions = mergeArraysToOptions(
        state.servicesOptions,
        datasetItems.map((item) => item.services),
      );
    },
    resetDatasetOptions: (state) => {
      state.groupsOptions = [];
      state.categoriesOptions = [];
      state.productsOptions = [];
      state.servicesOptions = [];
    },
    resetEditableDataset: (state) => {
      state.editableDataset = {};
      state.isEditableDatasetLoading = false;
    },

    resetParams: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getManagementDataset.pending, (state) => {
        state.isDatasetLoading = true;
      })
      .addCase(getManagementDataset.rejected, (state) => {
        state.isDatasetLoading = false;
      })
      .addCase(getManagementDataset.fulfilled, (state, action) => {
        state.isDatasetLoading = false;
        state.dataset = action.payload.map((el) => ({ ...el, isSelected: false }));
      })
      .addCase(collectData.pending, (state) => {
        state.isCollectingData = true;
      })
      .addCase(collectData.rejected, (state) => {
        state.isCollectingData = false;
      })
      .addCase(collectData.fulfilled, (state) => {
        state.isCollectingData = false;
      })
      .addCase(deleteFromDataset.rejected, (state) => {
        state.isDatasetLoading = false;
      })
      .addCase(deleteFromDataset.pending, (state) => {
        state.isDatasetLoading = true;
      })
      .addCase(deleteFromDataset.fulfilled, (state, action) => {
        state.dataset = state.dataset.filter(({ id }) => !action.payload.includes(id));
        state.isDatasetLoading = false;
      })
      .addCase(getSonarProgress.fulfilled, (state, action) => {
        action.payload.forEach((progress) => {
          if (progress.progressType === SONAR_PROGRESS_TYPE.CRAWL) {
            state.sonarProgress[SONAR_PROGRESS_TYPE.CRAWL] = {
              id: progress.id,
              status: progress.status,
              handled: progress.handled,
              total: progress.total,
            };
          }

          if (progress.progressType === SONAR_PROGRESS_TYPE.COLLECT_SONAR) {
            state.sonarProgress[SONAR_PROGRESS_TYPE.COLLECT_SONAR] = {
              id: progress.id,
              status: progress.status,
              currentProgress: progress.currentProgress,
              total: progress.total,
            };
          }
        });
      })
      .addCase(importDataset.rejected, (state, action) => {
        state.datasetErrors = action.payload;
        state.isDatasetLoading = false;
      })
      .addCase(importDataset.pending, (state) => {
        state.isDatasetLoading = true;
      })
      .addCase(importDataset.fulfilled, (state, action) => {
        const formattedDataset = action.payload.map((item) => ({ ...item, isSelected: false }));

        state.dataset = [...formattedDataset, ...state.dataset];
        state.isDatasetLoading = false;
        state.datasetErrors = {};
      })

      //GET EDITABLE DATASET
      .addCase(getDatasetItem.fulfilled, (state, action) => {
        state.editableDataset = action.payload;
        state.isEditableDatasetLoading = false;
      })
      .addCase(getDatasetItem.rejected, (state) => {
        state.isEditableDatasetLoading = false;
      })
      .addCase(getDatasetItem.pending, (state) => {
        state.isEditableDatasetLoading = true;
      })

      //EDIT DATASET
      .addCase(editDataset.fulfilled, (state, action) => {
        state.isDatasetLoading = false;
        state.dataset = state.dataset.map((item) =>
          item.id === action.payload.id ? { ...action.payload, isSelected: item.isSelected } : item,
        );
      })
      .addCase(editDataset.pending, (state) => {
        state.isDatasetLoading = true;
      })
      .addCase(editDataset.rejected, (state) => {
        state.isDatasetLoading = false;
      });
  },
});

export const {
  resetParams,
  toggleAllDomains,
  selectSingleDomain,

  updateDatasetOptions,
  addNewCategoryOption,
  addNewGroupOption,
  addNewServiceOption,
  addNewProductOption,
  resetDatasetOptions,
  resetEditableDataset,
} = sonarManagementSlice.actions;

export default sonarManagementSlice.reducer;
